<template>
  <div
    v-if="!isNullData"
    class="mine-card"
    :class="[
      mineData.type,
      {dashboard: isTypeDashboard},
      { analyzing: isAnalysis },
      { mashup: reportType === 'mashup' },
      { reactive: isReactive },
      { weekly: mineData.reportType === 'weekly' },
      { error: isError },
      { comparing: isComparing },
      { 'no-data': isError && !isReportError },
      { 'title-edit-mode': isTitleEditMode }
    ]"
  >
    <template v-if="reportType !== 'mashup'">
      <!-- No data, Retry unavailable -->
      <template v-if="isError">
        <div class="error-label error-control">
          <i class="icon icon--bell-till" v-btf-tooltip:top.start="{ contents: $t('mine.report.errorNoData.errorDesc'), offset: -12 }"></i>
          <span>{{ $t('mine.report.errorNoData.errorTag')}}</span>
        </div>
      </template>
      <!-- Data exists, Retry available -->
      <template v-else-if="isReportError">
        <div class="error-label error-control">
          <i class="icon icon--bell-till" v-btf-tooltip:top.start="{ contents: $t('mine.report.errorFail.errorDesc'), trigger: 'click', offset: -12 }"></i>
          <span>{{ $t('mine.report.errorFail.errorTag')}}</span>
        </div>
        <div class="retry-button error-control btf-button primary icon" @click="retryReport">
          <i class="icon icon--refresh-white"></i>
          {{  $t('common.retry') }}
        </div>
      </template>
    </template>
    <div class="clip-on-card" v-if="!isAnalysis && !isError && !isComparing && !isReactive" :class="{'click': isReportClicking}">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="49" viewBox="0 0 20 49" fill="none">
        <path opacity="0.12" d="M5.5 26C5.5 25.1716 4.82843 24.5 4 24.5C3.17157 24.5 2.5 25.1716 2.5 26H5.5ZM19.5 15.5V14H16.5V15.5H19.5ZM5.5 40.5V26H2.5V40.5H5.5ZM16.5 15.5V40.5H19.5V15.5H16.5ZM11 46C7.96243 46 5.5 43.5376 5.5 40.5H2.5C2.5 45.1944 6.30558 49 11 49V46ZM11 49C15.6944 49 19.5 45.1944 19.5 40.5H16.5C16.5 43.5376 14.0376 46 11 46V49Z" fill="black"/>
        <path d="M4.5 12.5V14H7.5V12.5H4.5ZM3.5 24C3.5 23.1716 2.82843 22.5 2 22.5C1.17157 22.5 0.5 23.1716 0.5 24H3.5ZM14.5 6.5V38.5H17.5V6.5H14.5ZM7.5 12.5V6.5H4.5V12.5H7.5ZM3.5 38.5V24H0.5V38.5H3.5ZM9 44C5.96243 44 3.5 41.5376 3.5 38.5H0.5C0.5 43.1944 4.30558 47 9 47V44ZM14.5 38.5C14.5 41.5376 12.0376 44 9 44V47C13.6944 47 17.5 43.1944 17.5 38.5H14.5ZM11 3C12.933 3 14.5 4.567 14.5 6.5H17.5C17.5 2.91015 14.5899 0 11 0V3ZM11 0C7.41015 0 4.5 2.91015 4.5 6.5H7.5C7.5 4.567 9.067 3 11 3V0Z" fill="#EC0047"/>
        <path d="M12.5 7.5C12.5 6.67157 11.8284 6 11 6C10.1716 6 9.5 6.67157 9.5 7.5H12.5ZM9.5 12.5V14H12.5V12.5H9.5ZM9.5 7.5V12.5H12.5V7.5H9.5Z" fill="#EC0047"/>
      </svg>
    </div>
    <template v-if="isContentCard">
      <div
        class="mine-content mine-card-header"
        :class="[
            `content-vol-${mineData.vol}`,
            `content-${promptType}-${mineData.vol}`,
            `mine-content-${mineData.type.toLowerCase()}`,
            promptType,
            smallTitle
          ]"
        :style="{ backgroundImage: `url(${bgImage})` }"
        @click="onShowReport"
      >
        <div class="date-view-info">
          <div class="date-info">
              <span class="text">
                {{ setRegDate}}
              </span>
          </div>
          <div class="view-count-info">
            <i class="icon icon--views-content"></i>
            <span class="text">
                {{mineData.viewCount | thousand}}
              </span>
          </div>
        </div>
        <div class="mine-main-info">
          <div class="mine-state" v-if="mineData.type === 'cxTrends' && mineData.tagList">
            <div
              class="state-label gray-border"
              v-for="tag in mineData.tagList"
              :key="tag"
            >
              {{ tag }}
            </div>
          </div>
          <div class="mine-title">
            <span class="title-wrap" v-html="mineData.title"></span>
            <p class="prompt-text" v-html="contentText.desc"></p>
          </div>
        </div>
        <div class="mine-bottom-deco">
          <span class="label" v-html="contentText.label"></span>
          <span class="vol" v-html="contentText.vol"></span>
        </div>
      </div>
    </template>
    <template v-else-if="isReactive">
      <div class="mine-card-header" :class="{'error-line': isError}" @click="onShowReport">
        <div class="date-view-info">
          <div class="date-info">{{ setRegDate }}</div>
          <div class="view-count-info">
            <i class="icon icon--eye-grey"></i>
            <span>{{ mineData.viewCount }}</span>
          </div>
        </div>
        <div class="mine-main-info">
          <div class="mine-state">
            <div class="state-label report" v-if="isTypeDashboard">Dashboard</div>
            <div class="state-label report">{{ isTypeDashboard ? $t(`mine.report.${mineData.type}`) : mineData.type }}</div>
            <div class="state-label weekly" v-if="mineData.reportType === 'weekly'">Weekly</div>
            <i v-if="mineData.new" class="icon icon--new-small"></i>
          </div>
          <div class="mine-title" v-if="mineData.reportType === 'weekly'">
            <p class="title">{{ weeklyDate.title }}</p>
            <p class="type">{{ mineData.type }}<span class="title-hidden">{{ weeklyDate.title }}</span></p>
            <p class="date-top">{{ weeklyDate.year }} <span class="date-hidden">{{ weeklyDate.month }}{{ weeklyDate.week }}</span></p>
            <p class="date-bottom">{{ weeklyDate.month }}{{ weeklyDate.week }}</p>
          </div>
          <div class="mine-title" v-else-if="isTypeDashboard">
            <p class="title">AI Customed Dashboard</p>
            <p class="type">{{ $t(`mine.report.${mineData.type}`) }}</p>
            <div class="title-wrap editable-area" v-if="!isTitleEditMode" @click="startTitleEditing"
                 @mouseenter="$emit('onTitleHover' , true)"
                 @mouseleave="$emit('onTitleHover' , false)"
            >
              <p :class="['date-top', {'placeholder': !this.mineData.title}]">
              <text-highlight :searchKeyword="keyword" :textItem="reportTitle" accentStyle="background" accentColor="#F46200"></text-highlight>
                <svg v-if="!isTitleEditMode" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7.58268 1.75H3.79101C2.82452 1.75 2.04102 2.53248 2.04102 3.49898C2.04102 5.48381 2.04102 8.50547 2.04102 10.5001C2.04102 11.4666 2.82275 12.25 3.78925 12.25C5.64242 12.25 8.36499 12.25 10.2098 12.25C11.1763 12.25 11.9577 11.4665 11.9577 10.5V6.41667" :stroke="dashboardEditColor" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M10.2305 2.09649C10.6925 1.6345 11.4415 1.6345 11.9035 2.09649C12.3655 2.55848 12.3655 3.30751 11.9035 3.76949L6.60567 9.06733L4.375 9.625L4.93267 7.39433L10.2305 2.09649Z" :stroke="dashboardEditColor" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </p>
              <button class="title-edit-button">
                <i class="icon icon--edit-white-size14"></i>
              </button>
            </div>
            <ui-input-text
              v-else
              class="title-input"
              ref="titleInput"
              :type="'text'"
              :modelValue="newReportTitle"
              :modelError="titleEditError"
              :errorTooltip="titleEditError"
              :placeholder="''"
              :errorType="'iconOnly'"
              :autoFocus="true"
              @input="setReportTitle"
              @onEnter="handleTitleEnter"
              @onBlur="finishTitleEditing"
            />
            <p v-if="!isAnalysis" class="prompt-text">"{{ mineData.dashboardPrompt }}"</p>
          </div>
        </div>
        <template v-if="mineData.type === 'dashboard'">
          <i class="icon icon--dashboard-bg" v-if="!isAnalysis"></i>
          <div v-else class="analyzing-wrap">
            <i class="icon icon--dashboard-bg2"></i>
            <div class="analyzing-text">
              {{ $t('mine.report.analyzing') }}
              <i class="icon icon--analyzing-gray"></i>
            </div>
          </div>
        </template>
        <template v-if="mineData.type === 'dashboardHell'">
          <i class="icon icon--dashboardHell-bg" v-if="!isAnalysis"></i>
          <div v-else class="analyzing-wrap">
            <i class="icon icon--dashboardHell-bg2"></i>
            <div class="analyzing-text">
              {{ $t('mine.report.analyzing') }}
              <i class="icon icon--analyzing-gray"></i>
            </div>
          </div>
        </template>
      </div>
      <div class="mine-card-body" @click="onShowReport">
        <template v-for="col in mineColumn">
          <template v-if="col.name === 'firstPath'">
            <div v-if="hasSubDomain" class="mine-info-column" :key="col.name" :class="col.name">
              <div class="column-container">
                <div class="info-label">
                  <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                </div>
                <div class="info-value">
                  {{ mineData.firstPath ? `/${mineData.firstPath}/` : $t('common.all') }}
                </div>
              </div>
            </div>
          </template>
          <div v-else class="mine-info-column" :key="col.name" :class="col.name">
            <div class="column-container">
              <div class="info-label">
                <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
              </div>
              <div class="info-value" :class="col.letterType">
                <template v-if="col.name === 'domain'">
                  <span class="ellipsis">{{ (hasSubDomain ? mineData.subDomain : `*.${mineData[col.name]}`) | domain }}</span>
                </template>
                <template v-else-if="col.name === 'period'">
                  <div class="date-range base">{{ mineData.startDateStr }} ~ {{ mineData.endDateStr }}</div>
                </template>
                <template v-else-if="col.name === 'marketing'">
                  <div v-if="!mineData.marketing.utm && !mineData.marketing.survey" class="colum-text">{{$t('common.all')}}</div>
                  <div v-else class="marketing-info-btn" :class="{ show: showMarketing }" @click.stop="openPopup">
                    {{ mineData.marketing && mineData.marketing.utm ? 'UTM Parameter' : 'Survey' }}
                    <i class="icon icon--arrow-up-black size-14" />
                  </div>
                </template>
                <template v-else-if="col.name === 'exist'">
                  {{ mineData[col.name] ? $t('common.return') : mineData[col.name] === false ? $t('common.new') : $t('common.all') }}
                </template>
                <template v-else-if="col.name === 'refer'">
                  <span class="ellipsis">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                </template>
                <template v-else-if="col.name === 'device'">
                  <span class="upper">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                </template>
                <template v-else>
                  {{ mineData[col.name] || '-' }}
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>
      <template v-if="!isError && isAnalysis && !isTypeDashboard">
        <div class="footer-status analyzing">
          <i class="icon icon--compare complete" v-if="getPromptType === 'compare'"></i>
          <span v-if="getPromptType === 'compare'">{{ $t('mine.report.compare.ing') }}</span>
          <span v-else>{{ $t('mine.report.analyzing') }}</span>
          <i class="icon icon--analyzing"></i>
        </div>
      </template>
    </template>
    <template v-else-if="!isComparing">
      <div class="mine-card-header" :class="{'error-line': isError}" @click="onShowReport">
        <div class="date-view-info">
          <div class="date-info">{{ setRegDate }}</div>
          <div class="view-count-info">
            <i v-if="!isError" class="icon icon--eye-white"></i>
            <i v-else class="icon icon--eye-darkgrey"></i>
            <span>{{ mineData.viewCount }}</span>
          </div>
        </div>
        <div class="mine-main-info">
          <div class="mine-state">
            <div class="state-label report" :class="{'report-analyze': isAnalysis}">
              <template v-if="mineData.type === 'target'">
                Journey-Target
              </template>
              <template v-else>
                {{ mineData.type }}
              </template>
            </div>
            <div class="state-label compare" v-if="getPromptType === 'compare'">
              <i class="icon icon--compare"></i>
              <span>{{ $t('mine.report.compare.label') }}</span>
            </div>
            <i v-if="mineData.new" class="icon icon--new-small"></i>
          </div>
          <div class="mine-title editable-area" v-if="!isTitleEditMode" @click.stop="startTitleEditing">
            <span class="title">
              <text-highlight :searchKeyword="keyword" :textItem="reportTitle" accentStyle="background" accentColor="#F46200"></text-highlight>
            </span>
            <button class="title-edit-button" v-if="reportType !== 'mashup'">
              <i class="icon icon--edit-white-size14"></i>
            </button>
          </div>
          <ui-input-text
            v-else
            class="title-input"
            ref="titleInput"
            :type="'text'"
            :modelValue="newReportTitle"
            :modelError="titleEditError"
            :errorTooltip="titleEditError"
            :placeholder="''"
            :errorType="'iconOnly'"
            :autoFocus="true"
            @input="setReportTitle"
            @onEnter="handleTitleEnter"
            @onBlur="finishTitleEditing"
          />
        </div>
      </div>
      <div class="mine-card-body" @click="onShowReport">
        <template v-if="mineData.promptType === 'compare'">
          <template v-for="col in mineColumn">
            <template v-if="col.name === 'firstPath'">
              <div v-if="hasSubDomain" class="mine-info-column" :key="col.name" :class="col.name">
                <div class="column-container">
                  <div class="info-label">
                    <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                  </div>
                  <div class="info-value">
                    {{ mineData.firstPath ? `/${mineData.firstPath}/` : 'Null' }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="mine-info-column" :key="col.name" :class="[col.name, {'target': col.name === compareKey}]">
              <div class="column-container"  :class="{'target': col.name === compareKey, 'error': isError}">
                <template v-if="col.name === 'firstPath'">
                  <div v-if="hasSubDomain" class="info-label">
                    <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                  </div>
                </template>
                <div v-else class="info-label">
                  <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                </div>
                <div class="info-value" :class="col.letterType">
                  <template v-if="col.name === 'domain'">
                    <span class="ellipsis">{{ (hasSubDomain ? mineData.subDomain : `*.${mineData.orgList[0][col.name]}`) | domain }}</span>
                  </template>
                  <template v-else-if="col.name === 'period'">
                    <div class="date-range base">{{ mineData.orgList[0].startDateStr }} ~ {{ mineData.orgList[0].endDateStr }}</div>
                  </template>
                  <template v-else-if="col.name === 'marketing'">
                    <div v-if="!mineData.orgList[0].utm && !mineData.orgList[0].survey" class="colum-text">All</div>
                    <div v-else class="marketing-info-btn" :class="{ show: showMarketing }" @click.stop="openPopup">
                      {{ mineData.orgList[0].utm ? 'UTM Parameter' : 'Survey' }}
                      <i class="icon icon--arrow-up-black size-14" />
                    </div>
                  </template>
                  <template v-else-if="col.name === 'exist'">
                    <span>{{ mineData.orgList[0][col.name] ? $t('common.return') : mineData.orgList[0][col.name] === false ? $t('common.new') : $t('common.all') }}</span>
                  </template>
                  <template v-else-if="col.name === 'refer'">
                    <span class="ellipsis">{{ mineData.orgList[0][col.name] ? mineData.orgList[0][col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else-if="col.name === 'device'">
                    <span class="upper ellipsis">{{ mineData.orgList[0][col.name] ? mineData.orgList[0][col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else>
                    {{ mineData.orgList[0][col.name] }}
                  </template>
                </div>
                <template v-if="col.name === compareKey">
                  <div class="target-column">
                    <div class="info-label">
                      <span>{{ $t(`mine.report.detail.${col.name}`) }}2</span>
                    </div>
                    <div class="info-value" :class="{upper: compareKey === 'device'}">
                      <div v-if="compareKey === 'period'" class="date-range target">
                        {{ mineData.orgList[1].startDateStr }} ~ {{ mineData.orgList[1].endDateStr }}
                      </div>
                      <span v-if="compareKey === 'exist'">
                        {{ mineData.orgList[1][compareKey] ? $t('common.return') : mineData.orgList[1][compareKey] === false ? $t('common.new') : $t('common.all') }}
                      </span>
                      <span v-if="compareKey === 'refer'">
                        {{ mineData.orgList[1]['refer'] ? mineData.orgList[1][compareKey] : $t('common.all') }}
                      </span>
                      <span v-if="compareKey === 'device'" >
                        {{ mineData.orgList[1]['device'] ? mineData.orgList[1][compareKey] : $t('common.all') }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </template>
        <template v-else>
          <template v-for="col in mineColumn">
            <template v-if="col.name === 'firstPath'">
              <div v-if="hasSubDomain" class="mine-info-column" :key="col.name" :class="col.name">
                <div class="column-container">
                  <div class="info-label">
                    <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                  </div>
                  <div class="info-value">
                    {{ mineData.firstPath ? `/${mineData.firstPath}/` : $t('common.all') }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="mine-info-column" :key="col.name" :class="col.name">
              <div class="column-container">
                <div class="info-label">
                  <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                </div>
                <div class="info-value" :class="col.letterType">
                  <template v-if="col.name === 'domain'">
                    <span class="ellipsis">{{ (hasSubDomain ? mineData.subDomain : `*.${mineData[col.name]}`) | domain }}</span>
                  </template>
                  <template v-else-if="col.name === 'url'">
                    <span class="ellipsis">{{ pagePathName }}</span>
                  </template>
                  <template v-else-if="col.name === 'period'">
                    <div class="date-range base">{{ mineData.startDateStr }} ~ {{ mineData.endDateStr }}</div>
                  </template>
                  <template v-else-if="col.name === 'marketing'">
                    <div v-if="!mineData.marketing.utm && !mineData.marketing.survey" class="colum-text">{{$t('common.all')}}</div>
                    <div v-else class="marketing-info-btn" :class="{ show: showMarketing }" @click.stop="openPopup">
                      {{ mineData.marketing && mineData.marketing.utm ? 'UTM Parameter' : 'Survey' }}
                      <i class="icon icon--arrow-up-black size-14" />
                    </div>
                  </template>
                  <template v-else-if="col.name === 'exist'">
                    {{ mineData[col.name] ? $t('common.return') : mineData[col.name] === false ? $t('common.new') : $t('common.all') }}
                  </template>
                  <template v-else-if="col.name === 'refer'">
                    <span class="ellipsis">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else-if="col.name === 'device'">
                    <span class="upper">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else>
                    {{ mineData[col.name] || '-' }}
                  </template>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>

      <template v-if="!isError">
        <template v-if="isAnalysis">
          <div class="footer-status analyzing">
            <i class="icon icon--compare complete" v-if="getPromptType === 'compare'"></i>
            <span v-if="getPromptType === 'compare'">{{ $t('mine.report.compare.ing') }}</span>
            <span v-else>{{ $t('mine.report.analyzing') }}</span>
            <i class="icon icon--analyzing"></i>
          </div>
        </template>
        <template v-else>
          <template v-if="reportType !== 'mashup' && (getPromptType === 'single' || getPromptType === 'compare')">
            <div class="footer-status" :class="[{'disabled': isCompareDisabled}]" @click="setCompareSetting">
              <i class="icon icon--compare" :class="{'complete' : isCompareDisabled}"></i>
              <span v-if="getPromptType === 'single'">{{ $t('mine.report.compare.confirm') }}</span>
              <span v-else>{{ $t('mine.report.compare.completed') }}</span>
            </div>
          </template>
        </template>
      </template>
    </template>
    <template v-else>
      <div class="mine-card-header">
        <div class="date-view-info">
          <div class="date-info ellipsis" style="width: 260px;">{{ reportTitle }}</div>
        </div>
        <div class="mine-state">
          <div class="state-label report">{{ mineData.type }}</div>
          <div class="state-label compare">
            <i class="icon icon--compare"></i>
            <span>{{ $t('mine.report.compare.stateCreate') }}</span>
          </div>
        </div>
        <div class="mine-title">
          <span class="title" v-if="compareTargetField && (compareTargetField.value || compareTargetField.value === false || compareTargetField.value === true)" v-html="$t(`mine.report.compare.title.${compareTargetField.name}.confirm`)"></span>
          <span class="title" v-else v-html="compareTargetField && compareTargetField.name ? $t(`mine.report.compare.title.${compareTargetField.name}.select`) : $t(`mine.report.compare.title.default`)">
          </span>
        </div>
      </div>
      <div class="mine-card-body">
        <template v-if="isCompareEditing">
          <ba-loading :applyBackground="true" :showLoadingText="true" />
        </template>
        <template v-else>
          <template v-for="col in mineColumn">
            <template v-if="col.name === 'firstPath'">
              <div v-if="hasSubDomain" class="mine-info-column" :key="col.name" :class="[col.name, {'compare-available' : COMPARE_AVAILABLE_COLUMN[col.name]}]">
                <div class="column-container">
                  <div class="info-label">
                    {{ $t(`mine.report.detail.${col.name}`) }}
                  </div>
                  <div class="info-value">
                    {{ mineData.firstPath ? `/${mineData.firstPath}/` : $t('common.all') }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="mine-info-column" :key="col.name" :class="[col.name, {'compare-available' : COMPARE_AVAILABLE_COLUMN[col.name]}, {'target': compareTargetField && compareTargetField.name === col.name}, {'error': compareTargetField && compareTargetField.fail}]">
              <div class="column-container" :class="[col.name, {'compare-available' : COMPARE_AVAILABLE_COLUMN[col.name]}, {'target': compareTargetField && compareTargetField.name === col.name}, {'error': compareTargetField && compareTargetField.name === col.name && compareTargetField.fail}]">
                <div class="info-label">
                  <span>{{ $t(`mine.report.detail.${col.name}`) }}</span>
                </div>
                <div class="info-value" :class="col.letterType">
                  <template v-if="col.name === 'domain'">
                    <span class="ellipsis">{{ (hasSubDomain ? mineData.subDomain : `*.${mineData[col.name]}`) | domain }}</span>
                  </template>
                  <template v-else-if="col.name === 'period'">
                    <div class="date-range base">{{ mineData.startDateStr }} ~ {{ mineData.endDateStr }}</div>
                  </template>
                  <template v-else-if="col.name === 'marketing'">
                    <div v-if="!mineData.marketing.utm && !mineData.marketing.survey" class="colum-text">{{$t('common.all')}}</div>
                    <div v-else class="marketing-info-btn" :class="{ show: showMarketing }" @click.stop="openPopup">
                      {{ mineData.marketing && mineData.marketing.utm ? 'UTM Parameter' : 'Survey' }}
                      <i class="icon icon--arrow-up-black size-14" />
                    </div>
                  </template>
                  <template v-else-if="col.name === 'exist'">
                    {{ mineData[col.name] ? $t('common.return') : mineData[col.name] === false ? $t('common.new') : $t('common.all') }}
                  </template>
                  <template v-else-if="col.name === 'refer'">
                    <span class="ellipsis">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else-if="col.name === 'device'">
                    <span class="upper">{{ mineData[col.name] ? mineData[col.name] : $t('common.all') }}</span>
                  </template>
                  <template v-else>
                    <span class="ellipsis">{{ mineData[col.name] || '-' }}</span>
                  </template>
                </div>

                <i class="icon icon--edit white" v-if="COMPARE_AVAILABLE_COLUMN[col.name]" @click="setCompareTargetSetting(col)"/>

                <template v-if="compareTargetField && col.name === compareTargetField.name">
                  <div class="target-column">
                    <div class="info-label">
                      <span>{{ $t(`mine.report.detail.${compareTargetField.name}`) }}2</span>
                    </div>
                    <div class="info-value" :class="[compareTargetField.letterType, {'target': compareTargetField && col.name === compareTargetField.name}]">
                      <template v-if="compareTargetField.name === 'period'">
                        <div class="target-input period" @click.stop="compareSelect('period')">
                          <i class="icon icon--calendar-input small" />
                          <span v-if="!compareTargetField.value">{{ $t(`mine.report.compare.field.period.placeHolder`)}}</span>
                          <span v-else class="value">{{compareTargetField.value.startDate}} ~ {{compareTargetField.value.endDate}}</span>
                        </div>

                        <div class="compare-date-picker-wrap">
                          <CompareDatePicker v-if="openTargetCalendar"
                                             :analysisRange="targetDateRange"
                                             :maxDays="184"
                                             :minDate="minSelectedDate"
                                             :maxDate="maxSelectedDate"
                                             :minSelectableDate="null"
                                             :timezone="userData.timezone"
                                             :timezoneName="userData.timezoneName"
                                             @onRangeChange="onTargetDateChange"
                                             @onRangeCancel="openTargetCalendar = false"
                          />
                        </div>
                      </template>
                      <template v-else-if="compareTargetField.name === 'exist'">
                        <ba-dropdown
                                     :value="compareTargetField.value"
                                     :placeholder="$t(`mine.report.compare.field.exist.placeHolder`)"
                                     :items="searchExistItems"
                                     :width="'100%'"
                                     @input="onTargetDateChange" />
                      </template>
                      <template v-else-if="compareTargetField.name === 'refer'">
                        <ba-dropdown
                            :value="compareTargetField.value"
                            :placeholder="$t(`mine.report.compare.field.refer.placeHolder`)"
                            :items="searchReferitems"
                            :width="'100%'"
                            :isBigFontSize="true"
                            @input="onTargetDateChange" />
                      </template>
                      <template v-else-if="compareTargetField.name === 'device'">
                        <ba-dropdown
                            :value="compareTargetField.value"
                            :placeholder="$t(`mine.report.compare.field.device.placeHolder`)"
                            :items="searchDeviceItems"
                            :width="'100%'"
                            @input="onTargetDateChange" />
                      </template>
                    </div>
                  </div>
                </template>
              </div>

            </div>
          </template>
        </template>
      </div>

      <div class="footer-status compare-available">
        <ui-button :primary="true"  v-if="compareTargetField && (compareTargetField.value !== '' && compareTargetField.value !== undefined) && !compareTargetField.fail" @click="createCompareReport">
          {{$t(`mine.report.compare.field.${compareTargetField.name}.confirm`)}}
        </ui-button>
      </div>

    </template>

    <div v-if="showMarketing" class="mine-popup" @click="showMarketing = false">
      <div class="mine-marketing-detail">
        Title
        <div class="contents"></div>
      </div>
    </div>
    <button v-if="isComparing" class="corner-button" @click="cancelCompareSetting">
      <i class="icon icon--close-size16 white" />
    </button>
    <button :class="['corner-button goto', {dashboard : isTypeDashboard}] " v-else-if="!isAnalysis && !isError && !isContentCard && !isTypeDashboard" @click="onShowReport">
      <i class="icon icon--ba-arrow size-20"></i>
    </button>
  </div>
</template>

<script>
import { addDays, format, max, parse } from 'date-fns'
import { REPORT_LINK, MONTHS, DEVICE_LIST, SOLOMON_REPORT } from '@/libs/constants'
import { getBasicInfo } from '@/api/journey'
import { editReportTitle } from '@/api/ai'
import BaLoading from '@/components/ui/ba/Loading.vue'
import TextHighlight from '@/components/ui/TextHighlight'
import CompareDatePicker from '@/components/mine/CompareDatePicker.vue'
import { mapState } from 'vuex'
import { convertToTimeZone } from 'date-fns-timezone'
import UiButton from '@/components/ui/UiButton.vue'
import UiInputText from '@/components/ui/UiInputText.vue'
import BaDropdown from '@/components/ui/ba/Dropdown.vue'
import { getPercentage } from '@/libs/utils'
import { isInIframe } from '@/libs/auth-helper'
import dashboard from '@/views/member/dashboard/index.vue'

const REPORT_DETAIL_DATA = [
  // { name: 'type', title: '종류', letterType: 'upper' },
  { name: 'domain', title: '도메인', letterType: '' },
  { name: 'firstPath', title: '시작패스', letterType: '' },
  { name: 'period', title: '기간', letterType: '' },
  { name: 'marketing', title: '마케팅', letterType: 'upper' },
  { name: 'device', title: '기기', letterType: 'upper' },
  { name: 'refer', title: '유입경로', letterType: '' },
  { name: 'exist', title: '방문타입', letterType: 'upper' }
]

export default {
  name: 'MineListCardItem',
  components: { BaDropdown, UiInputText, UiButton, CompareDatePicker, BaLoading, TextHighlight },
  props: {
    mineData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    timezone: {
      type: String,
      default: null
    },
    reportType: {
      type: String,
      default: null
    },
    isError: {
      type: Boolean,
      default: false
    },
    isReportError: {
      type: Boolean,
      default: false
    },
    reportStatus: {
      type: String,
      default: null
    },
    comparingState: {
      type: Boolean,
      default: false
    },
    keyword: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isNoneTitle: false,
      mineColumn: [],
      showMarketing: false,
      compareKey: '',
      isReportClicking: false,
      isTitleEditMode: false,
      isComparing: false,
      isCompareEditing: false,
      COMPARE_AVAILABLE_COLUMN: {
        period: true,
        device: true,
        refer: true,
        exist: true
      },
      compareTargetField: null,
      targetDateRange: {
        starDate: addDays(new Date(), -6),
        endDate: new Date()
      },
      openTargetCalendar: false,
      minSelectedDate: null,
      maxSelectedDate: null,
      datepickerPosition: {
        x: 0,
        y: 0
      },
      searchExistItems: [],
      searchDeviceItems: [],
      searchReferitems: [],
      newReportTitle: '',
      titleEditError: ''
    }
  },
  computed: {
    ...mapState([
      'domain',
      'userData',
      'journeyOptions',
      'journeyPeriod'
    ]),
    EXIST_LIST () {
      return [
        {
          text: this.$t('common.all'),
          value: null
        },
        {
          text: this.$t('common.new'),
          value: false
        },
        {
          text: this.$t('common.return'),
          value: true
        }
      ]
    },
    dashboardEditColor () {
      if (this.isAnalysis) {
        return '#bbb'
      }
      if (this.mineData.type === 'dashboardHell') {
        return this.mineData.title ? '#FF708A' : '#FF8FA3'
      } else {
        return this.mineData.title ? '#999999' : '#B1B1B1'
      }
    },
    reportTitle () {
      if (this.mineData.version && this.mineData.version > 1) {
        let version = this.mineData.version
        version = version < 10 ? ` v. ${version}` : ` v.${version}`

        return version + ' ' + this.mineData.title
      } else if (this.isTypeDashboard && !this.mineData.title) {
        return this.$t('mine.report.dashboardTitle')
      } else {
        return this.mineData.title
      }
    },
    reportLink () {
      if (this.isAnalysis) {
        return null
      } else {
        return REPORT_LINK + '/' + this.mineData.id
      }
    },
    weeklyDate () {
      const weeklyDate = this.mineData.weeklyDateStr || ''
      const weeklyDateArr = weeklyDate.split('-')
      const lang = this.mineData.lang

      let year = weeklyDateArr[0] || ''
      let month = weeklyDateArr[1] || 1
      let week = weeklyDateArr[2] || ''

      return {
        title: lang !== 'ko' ? lang === 'ja' ? '週間レポート' : 'Weekly Report' : '주간리포트',
        year: lang !== 'ko' ? lang === 'ja' ? `${year}年` : year : `${year}년`,
        month: lang !== 'ko' ? lang === 'ja' ? `${month}月 ` : `${MONTHS[month - 1]}.` : `${month}월 `,
        week: lang !== 'ko' ? lang === 'ja' ? `第${week}週` : `Week ${week}` : `${week}주차`,
        monthNum: month
      }
    },
    setRegDate () {
      const date = parse(this.mineData.regDate, this.timezone)
      return format(date, 'YYYY-MM-DD HH:mm:ss')
    },
    hasSubDomain () {
      return !!this.mineData.subDomain
    },
    isAnalysis () {
      return this.reportStatus === 'ing'
    },
    isTypeDashboard () {
      return this.mineData.type === 'dashboard' || this.mineData.type === 'dashboardHell' || this.mineData.type === 'dashboardGa'
    },
    getPromptType () {
      return this.mineData.promptType
    },
    isNullData () {
      return this.mineData.promptType === 'compare' && this.mineData.orgList.length === 0
    },
    isCompare () {
      return this.mineData.promptType === 'compare'
    },
    isReactive () {
      return this.mineData.reportType === 'weekly' || this.isTypeDashboard
    },
    isCompareDisabled () {
      return this.mineData.type === 'target' || this.mineData.promptType === 'compare'
    },
    isContentCard () {
      return this.mineData.type === 'cxTrends'
    },
    bgImage () {
      const { type, vol } = this.mineData

      const fileName =
        this.promptType === 'mini'
          ? `${type}-${this.promptType}-${vol}.png`
          : `${type}-${vol}.png`

      return require(`@/assets/img/mine/${fileName}`)
    },
    promptType () {
      if (!this.mineData || !this.mineData.reportS3Path) return null

      const match = this.mineData.reportS3Path.match(/geo\/([^/]+)/)
      if (!match) return null

      const rawType = match[1]
      const typeMap = {
        'cx-trends-mini': 'mini',
        'cx-trends': 'single'
      }
      return typeMap[rawType] || rawType
    },
    smallTitle () {
      const { vol } = this.mineData

      // cxTrends
      if (this.promptType === 'single' && [5, 7, 8, 9].includes(vol)) {
        return 'small-card-title'
      }

      // cxTrends - mini
      if (this.promptType === 'mini' && [1].includes(vol)) {
        return 'small-card-title'
      }

      return ''
    },
    contentText () {
      if (this.promptType === 'mini') {
        return {
          label: 'CX Trends Benchmark - Big Data',
          desc: '',
          vol: 'Mini Report'
        }
      }

      return {
        label: 'CX Data Trend<br> Research',
        desc: 'CX Trends Benchmark - Big Data',
        vol: `Vol. ${this.mineData.vol}`
      }
    },
    pagePathName () {
      if (this.mineData.pageUrl) {
        const a = document.createElement('a')
        a.href = this.mineData.pageUrl
        let path = a.pathname

        if (this.journeyOptions.matchType === 'exact') {
          const pathStartIdx = a.href.indexOf(a.host) + a.host.length
          path = a.href.slice(pathStartIdx)
        }
        return path
      }
      return ''
    }
  },
  watch: {
    mineData () {
      if (this.mineData.promptType === 'compare' && this.mineData.orgList.length === 2) {
        this.mineColumn.forEach(col => {
          let result
          if (col.name === 'period') {
            result = [this.mineData.orgList[0].startDateStr, this.mineData.orgList[0].endDateStr].join('~') !== [this.mineData.orgList[1].startDateStr, this.mineData.orgList[1].endDateStr].join('~')
          } else {
            result = this.mineData.orgList[0][col.name] !== this.mineData.orgList[1][col.name]
          }
          if (result) {
            this.compareKey = col.name
          }
        })
      }
    },
    comparingState () {
      this.isComparing = this.comparingState
      this.compareTargetField = null
      this.isCompareEditing = false
    }
  },
  created () {
    this.mineColumn = [...REPORT_DETAIL_DATA]
    if (this.mineData.type === 'target') {
      this.mineColumn.splice(2, 0, { name: 'url', title: 'URL' })
    }

    if (this.reportType === 'prefix') {
      this.mineColumn.splice(4, 1)
    }

    this.setMinMaxDate()
  },
  mounted () {
    if (this.mineData.promptType === 'compare' && this.mineData.orgList.length === 2) {
      this.mineColumn.forEach(col => {
        let result
        if (col.name === 'period') {
          result = [this.mineData.orgList[0].startDateStr, this.mineData.orgList[0].endDateStr].join('~') !== [this.mineData.orgList[1].startDateStr, this.mineData.orgList[1].endDateStr].join('~')
        } else {
          result = this.mineData.orgList[0][col.name] !== this.mineData.orgList[1][col.name]
        }
        if (result) {
          this.compareKey = col.name
        }
      })
    }
  },
  deactivated () {
    if (this.isTitleEditMode) {
      this.exitTitleEditing()
    }
  },
  methods: {
    setReportTitle (value) {
      if (value.length > 70) {
        this.titleEditError = this.$t('mine.maxLengthError', { limit: 70 })
      } else {
        this.titleEditError = ''
      }
      this.newReportTitle = value
    },
    startTitleEditing () {
      this.newReportTitle = this.reportTitle
      this.isTitleEditMode = true
      this.$emit('changeTitleEditMode', this.mineData.id, true)
    },
    handleTitleEnter () {
      this.$refs.titleInput.$refs.input.blur()
    },
    finishTitleEditing () {
      if (this.titleEditError) return
      if (!this.newReportTitle.trim()) {
        this.exitTitleEditing()
        return
      }
      setTimeout(() => {
        this.isTitleEditMode = false
        this.$emit('changeTitleEditMode', this.mineData.id, false)
      }, 150)

      if (this.reportTitle === this.newReportTitle) return
      editReportTitle({
        reportId: this.mineData.id,
        title: this.newReportTitle
      }).then(res => {
        this.mineData.title = res.title
      }).catch(err => {
        console.log(err)
      })
    },
    exitTitleEditing () {
      this.isTitleEditMode = false
      this.titleEditError = ''
      this.newReportTitle = ''
      this.$emit('changeTitleEditMode', this.mineData.id, false)
    },
    setInitData () {
      if (this.mineData.type === 'ranking') {
        this.COMPARE_AVAILABLE_COLUMN.device = false
        this.COMPARE_AVAILABLE_COLUMN.refer = false
        this.COMPARE_AVAILABLE_COLUMN.exist = false
      } else {
        this.COMPARE_AVAILABLE_COLUMN.device = true
        this.COMPARE_AVAILABLE_COLUMN.refer = true
        this.COMPARE_AVAILABLE_COLUMN.exist = true
      }

      this.searchDeviceItems = []
      this.searchExistItems = []

      this.searchDeviceItems = DEVICE_LIST.filter(item => {
        return item.value !== this.mineData.device
      })

      this.searchExistItems = this.EXIST_LIST.filter(item => {
        return item.value !== this.mineData.exist
      })

      this.setMinMaxDate()
    },
    setMinMaxDate () {
      const now = new Date()
      let periodMin = convertToTimeZone(max(this.domain.installDate, addDays(now, -397)), { timeZone: this.domain.timezoneName })
      let periodMax = convertToTimeZone(now, { timeZone: this.domain.timezoneName })
      this.minSelectedDate = parse(periodMin, 'YYYY-MM-DD')
      this.maxSelectedDate = parse(periodMax, 'YYYY-MM-DD')
      return {
        minDate: parse(periodMin, 'YYYY-MM-DD'),
        maxDate: parse(periodMax, 'YYYY-MM-DD')
      }
    },
    setCompareTargetSetting (targetColumn) {
      this.compareTargetField = { ...targetColumn }
    },
    cancelCompareSetting () {
      this.$emit('onCompare', false, this.mineData)
    },
    setCompareSetting () {
      if (this.isCompareDisabled) return

      if (this.getPromptType === 'single') {
        this.$emit('')
        this.$emit('onCompare', true, this.mineData)
        const item = this.mineData
        const type = item.type
        const parentSidNoPrefix = this.$route.query.sid
        const subSid = item.subSid
        const firstPath = item.firstPath
        const device = item.device

        this.setInitData()

        if (type === 'journey' || type === 'trend') {
          this.isCompareEditing = true

          // get  refer list
          const params = {
            'start': item.startDateStr,
            'end': item.endDateStr,
            'sid': parentSidNoPrefix,
            'matchType': 'simple',
            'exist': item.exist,
            'utm': item.marketing.utm ? item.utm : null,
            'survey': item.marketing.survey ? item.survey : null,
            'isTrend': type === 'trend',
            'serviceType': 'ba'
          }
          if (subSid) {
            params.subSid = subSid
            if (firstPath) {
              params.firstPathname = firstPath
            }
          }

          getBasicInfo(params).then(result => {
            this.searchReferitems = []
            const referList = result.device[device].refer
            const sessionCount = device === 'all' ? result.session : result.total[device]

            if (this.mineData.refer !== null) {
              this.searchReferitems.push({
                value: null,
                text: this.$t('common.all'),
                additionalText: ``
              })
            }

            for (const item of referList) {
              for (let [refer, session] of Object.entries(item)) {
                let referText = refer
                if (refer.substr(0, 4) === 'http') {
                  const referObj = document.createElement('a')
                  referObj.href = refer
                  referText = refer.substr(referObj.protocol.length + 2)
                }
                if (this.mineData.refer !== refer) {
                  this.searchReferitems.push({
                    value: refer,
                    text: referText,
                    additionalText: `(${getPercentage(session, sessionCount).toFixed(1)}%)`
                  })
                }
              }
            }

            this.isCompareEditing = false
          })
        }
      }
    },
    onShowReport () {
      if (this.isAnalysis || this.isError || this.isReportError || this.isComparing || this.isTitleEditMode) {
        return
      }

      if (this.isContentCard) {
        window.open(
          SOLOMON_REPORT + `/${this.mineData.type}/${this.mineData.id}`,
          '_blank'
        )
      } else {
        window.open(REPORT_LINK + '/' + this.mineData.id, isInIframe() ? '_self' : '_blank')
        this.isReportClicking = false
      }
    },
    openPopup ({ target }) {
      this.$emit('popup', { target, data: this.mineData })
    },
    compareSelect (targetColumn) {
      if (targetColumn === 'period') {
        this.openTargetCalendar = true
      }

      this.targetDateRange = {
        startDate: parse(this.mineData.startDateStr, this.domain.timezoneName),
        endDate: parse(this.mineData.endDateStr, this.domain.timezoneName)
      }
    },
    onTargetDateChange (value) {
      this.$set(this.compareTargetField, 'fail', false)
      let compareValue = null

      if (this.compareTargetField.name === 'period') {
        compareValue = {
          startDate: format(value.range.startDate, 'YYYY-MM-DD'),
          endDate: format(value.range.endDate, 'YYYY-MM-DD')
        }
        if (compareValue.startDate === this.mineData.startDateStr && compareValue.endDate === this.mineData.endDateStr) {
          this.$set(this.compareTargetField, 'fail', true)
        }
      } else {
        compareValue = value
      }

      this.$set(this.compareTargetField, 'value', compareValue)

      this.openTargetCalendar = false
    },
    createCompareReport () {
      this.$emit('compare', { compareTarget: this.compareTargetField, data: this.mineData })
    },
    retryReport () {
      this.$emit('retry', this.mineData)
    }
  }
}
</script>

<style lang="scss" scoped>
$errorColor: #E60724;
$mashupColor: #FF30EA;
$reportColor: #0085FF;
$analyzeColor: #B65EFC;

$reportType: (
    'journey': (#8F74FA, #D3C8FF, #260A93, #6A98FF, #7767FF, #8241FF, url('../../assets/img/mine/mine_journey_bg.svg')),
    'target': (#8F74FA, #D3C8FF, #260A93, #6A98FF, #7767FF, #8241FF, url('../../assets/img/mine/mine_journey_bg.svg')),
    'ranking': (#6289EB, #C9E656, #052472, #33C2FF, #3390DF, #456DD1, url('../../assets/img/mine/mine_ranking_bg.svg')),
    'trend': (#00A5D9, #64DAFF, #005773, #73EAFF, #1DB2E0, #0190D3, url('../../assets/img/mine/mine_trend_bg.svg')),

);

.mine-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  height: 100%;
  background-color: #fff;
  cursor: pointer;
  // box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.06), 0px 18px 22px -7px rgba(0, 0, 0, 0.16);

  &.reactive {
    overflow: hidden;
    & * {
      transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
    }
    .mine-card-header {
      position: relative;
      background: none;
      flex: 1;
      &::before {
        display: none;
      }

      .mine-card-corner {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 0;
        height: 0;
        border-left: 55px solid transparent;
        border-right: 0 solid transparent;
        border-bottom: 60px solid #fff;
        & .icon {
          position: absolute;
          left: -28px;
          bottom: -51px;
        }
        .icon--arrow-body-right-blue {
          width: 20px;
          height: 20px;
        }
      }

      .mine-main-info {
        position: relative;
        .mine-state {
          .state-label.weekly {
            color: #666;
          }
        }
        .mine-title {
          margin-top: 44px;
          line-height: 1.2;
          font-weight: 600;
          & * {
            transform-origin: top left;
          }
          .title {
            font-size: 18px;
            font-weight: 500;
          }
          .type {
            position: relative;
            display: inline-block;
            margin-bottom: 22px;
            font-size: 60px;
            line-height: 1;
            text-transform: capitalize;
            .title-hidden {
              display: none;
            }
            &:after {
             content: '';
              position: absolute;
              left: 0;
              bottom: -9px;
              width: 100%;
              height: 4px;
              background-color: #fff;
              transition: inherit;
            }
          }
          .date-top {
            font-weight: 500;
            margin-right: -20px;
            font-size: 30px;

            .date-hidden {
              opacity: 0;
            }
          }
          .date-bottom {
            font-size: 30px;
          }
        }
        .month-label {
          position: absolute;
          bottom: 0;
          left: 0;
          text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.20);
          font-size: 68px;
          font-weight: 600;
          line-height: 1;
        }
      }
    }
    .mine-card-body {
      position: absolute;
      background: #fff;
      width: 100%;
      height: 304px;
      padding-top: 24px;
      bottom: 0;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      transform: translate(100%, 100%);
    }
    .corner-button {
      opacity: 0;
      i {
        right: 16px;
      }
    }
    &.dashboard {
      .mine-card-header {
        color: #444;
        &:has(.editable-area:hover):hover ~ .corner-button.goto .icon {
          display: none;
        }
        .mine-main-info {
          .mine-title {
            text-shadow: none;
            margin-top: 55px;
            color: #444;
            line-height: 1.2;
            font-weight: 600;
            .title {
              color: #999;
              font-size: 16px;
              line-height: 1;
            }
            .type {
              font-size: 30px;
              font-weight: 700;
              line-height: 100%;
              margin: 8px 0;

              &:after {
                display: none;
              }
            }
            .title-wrap {
              position: relative;
              .date-top {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                width: 280px;
                position: relative;
                font-size: 18px;
                cursor: pointer;
                color: #444;
                span {
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-break: break-all;
                  min-width: 0;
                  display: -webkit-box;
                  line-clamp: 3;
                  -webkit-line-clamp: 3;
                  -webkit-box-orient: vertical;
                  transition: none;
                }

                svg {
                  margin-top: 4px;
                  flex-shrink: 0;
                }
                &.placeholder {
                  color: #bbb;
                }
              }
              &:hover {
                .date-top {
                  transition: none;
                  background-color: rgba(0, 0, 0, 0.16);
                  box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.16);
                  border-radius: 2px;
                  &.placeholder {
                    color: #777777;
                  }

                  svg {
                    display: none;
                  }
                }

                .title-edit-button {
                  display: flex;
                }
              }
            }
            .title-input {
              margin-top: 0;
              margin-left: -4px;
              transform: translateY(-5px);
            }
            .prompt-text {
              margin-top: 24px;
              color: #444;
              font-size: 13px;
              font-weight: 400;
              line-height: 150%;
              text-overflow: ellipsis;
              overflow: hidden;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              line-clamp: 3;
              -webkit-line-clamp: 3;
            }
          }
        }
        .date-view-info {
          .view-count-info {
            &:before {
              opacity: 0.4;
              border-color: #BBB;
            }
          }
        }
        .mine-state {
          .state-label {
            padding: 4px 6px;
            border: 1px solid #D7D7D7;
          }
        }
        .icon--dashboard-bg, .icon--dashboardHell-bg {
          position: absolute;
          right: 24px;
          bottom: 24px;
        }
        .analyzing-wrap {
          position: absolute;
          left: 50%;
          bottom: 96px;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          .analyzing-text {
            @include flexXYcenter;
            gap: 12px;
            color: #BBB;
            font-size: 16px;
            font-weight: 600;
            line-height: 150%;
          }
        }
      }

      .mine-card-body {
        &::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
        }
      }

      &.title-edit-mode {
        .corner-button.goto {
          display: none;
        }
      }

      &.analyzing {
        background-color: #F4F4F4;
        cursor: default;
        .mine-card-header {
          .mine-main-info {
            .mine-state {
              opacity: 0.3;
            }
            .mine-title {
              color: #BBB;
              .title {
                font-size: 16px!important;
                color: #BBB;
              }
              .type {
                font-size: 30px!important;
              }
              .title-wrap {
                &:hover {
                  .date-top, .date-top.placeholder {
                    color: #BBB;
                  }
                }
                .date-top, .date-top.placeholder {
                  color: #BBB;
                }
              }
            }
          }
        }
      }

      &:hover:not(.error), &.title-edit-mode {
        .mine-card-header {
          background: #F4F4F4;
          .mine-main-info {
            .mine-title {
              transform: translateY(-39px);
              .title {
                scale: 1;
              }
              .type {
                scale: 1;
              }
              .title-wrap {
                .date-top {
                  scale: 1;
                  font-weight: 500;
                  line-height: 1.2;
                  transform: translateY(0);
                  overflow: hidden;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  overflow-wrap: break-word;
                  word-break: break-all;
                }
                .title-edit-button {
                  top: -3px;
                }
              }
            }
          }
        }
      }
    }

    &.dashboardHell {
      .mine-card-header {
        .mine-main-info {
          .mine-title {
            color: #EC0047;
            .title {
              color: #FF708A;
            }

            .title-wrap {
              .date-top {
                color: #EC0047;

                &.placeholder {
                  color: #FFAEBD
                }
              }

              &:hover {
                .date-top {
                  &.placeholder {
                    color: #FF4A6B ;
                  }
                }
              }
            }
          }
        }
        .mine-state {
          .state-label {
            padding: 4px 6px;
            border: 1px solid #D7D7D7;
          }
        }
      }

      .mine-card-body {
        &::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
        }
      }

      &.title-edit-mode {
        .corner-button.goto {
          background:  #f4f4f4;
        }
      }

      &.analyzing {
        background-color: #F4F4F4;
        .mine-card-header .mine-main-info .mine-title .title-wrap .date-top {
          color: #bbb
        }
      }

      &:hover:not(.error), &.title-edit-mode {
        .mine-card-header {
          background: #F4F4F4;
          .mine-main-info {
            .mine-title {
              .title {
                scale: 1;
              }
              .type {
                scale: 1;
              }
              .title-wrap {
                .date-top {
                  scale: 1;
                  font-weight: 500;
                  line-height: 1.2;
                  overflow: hidden;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  overflow-wrap: break-word;
                  word-break: break-all;
                }
                .title-edit-button {
                  top: -3px;
                }
              }
            }
          }
        }
      }
    }

    &:hover:not(.error), &.title-edit-mode {
      .mine-card-header {
        .mine-main-info {
          .mine-title {
            transform: translateY(-39px);
            .title {
              scale: 0.95;
            }
            .type {
              scale: 0.73;
              &:after {
              transform: translate(-115%, 120px)
              }
            }
            .date-top {
              scale: 0.8;
              line-height: 1.2;
              transform: translateY(-43px);

              .date-hidden {
                opacity: 1;
              }
            }
            .date-bottom {
              scale: 0.8;
              opacity: 0;
              transform: translateY(-43px);
            }
          }
          .month-label {
            opacity: 0;
          }
        }
      }
      .mine-card-body {
        transform: translate(0, 0);
      }

      .corner-button {
        opacity: 1;
        i {
          transform: translateX(10px);
        }
      }
    }

    &.error {
      cursor: default;
      .mine-card-header {
        padding-top: 13px;
        background: none !important;
        .mine-card-corner {
          display: none;
        }
      }

      &:not(.no-data) {
        .mine-card-header {
          .date-view-info {
            color: #fff;
          }
          .mine-main-info {
            .mine-state > .state-label {
              background-color: #fff !important;
              color: #888;
            }

            .mine-title {
              color: #888!important;
              margin-top: 15px;
            }
          }
        }
        &:before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: #ebebebc2;
          z-index: 1;
        }
      }

      &.no-data {
        .mine-card-header {
          .mine-main-info {
            .mine-state {
              .state-label {
                background-color: #EBEBEB !important;
                color: #999;
              }
            }
            .mine-title {
              .title {
                display: none;
              }
              .type {
                position: relative;
                font-size: 36px;
                &::after {
                  display: none;
                }
                .title-hidden {
                  display: inline-block;
                  position: absolute;
                  right: -8px;
                  transform: translateX(100%);
                  top: 11px;
                  color: #CCC;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 1.2;
                }
              }
            }
          }
        }
      }
    }

    &.error.no-data, &.analyzing:not(.error):not(.dashboard) {
      .mine-card-header {
        .mine-main-info {
          position: relative;
          .mine-state {
            height: 32px;
          }
          .mine-title {
            margin-top: 0;
            line-height: 1.2;
            font-weight: 600;
            .type {
              margin-bottom: 8px;
            }
            .date-top {
              font-size: 24px;
              line-height: 1;
              .date-hidden {
                opacity: 1;
              }
            }
            .date-bottom {
              display: none;
            }
          }
          .month-label {
            position: absolute;
            bottom: 0;
            left: 0;
            text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.20);
            font-size: 68px;
            font-weight: 600;
            line-height: 1;
          }
      }
    }
    .mine-card-body {
      transform: none;
    }
  }

    &.analyzing {
      cursor: default;
    }

    &.analyzing:not(.error) {
      .mine-card-header {
        .mine-main-info {
          .mine-state {
            .state-label {
              border: 1px solid rgba(0, 0, 0, 0.12);
            }
          }
          .mine-title {
            .title {
              font-size: 18px;
              font-weight: 500;
            }
            .type {
              font-size: 44px;
            }
          }
        }
        .mine-card-corner {
          display: none;
        }
      }
    }

    &.title-edit-mode {
      cursor: default;
      .corner-button {
        cursor: default;
      }
    }
  }
  &:not(.reactive) {
    .mine-card-header {
      .mine-title {
        &:hover {
          //background-color: rgba(0, 0, 0, 0.16);
          //box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.16);
          //border-radius: 4px;
          .title-edit-button {
            display: flex;
          }
        }
      }
    }
  }

  &.comparing {
    cursor: default;
    & .mine-card-header {
      background-color: #222;

      & .state-label {
        &.compare {
          border: 0 !important;
          outline: 0 !important;
        }
      }

      & .date-view-info {
        width: 270px;
      }
    }

    & .mine-card-body {
      background-color: #222;

      & .mine-info-column {
        border-bottom-color: #444;
        position: relative;
        height: 33px;

        &.target {
          height: 68px;
        }

        & .compare-available {

          & .target-column {
            position: absolute;
            //display: flex;
            gap: 12px;
            top: 32px;
            width: 100%;
          }

          &:not(.target):hover {
            background-color: #444;
          }
          position: absolute;
          top: 2px;
          left: -4px;
          width: calc(100% + 16px);
          height: 29px;
          box-sizing: border-box;
          border-radius: 4px;
          border: 1px solid #666;
          background: #000;
          vertical-align: middle;
          padding: 4px 8px;
          margin-left: -4px;

          &.target {
            height: 63px;
            background-color: #104271;
            border-color: #289EEF;

            & .info-value,
            & .info-label {
              & > span {
                color: #fff;
                margin-top: 3px;
              }
            }

            & .icon--edit {
              display: none;
            }
          }

          &.error {
            background-color: #5F1B24;
            border-color: #E60724;
          }

          & > .info-label,
          & > .info-value {
            color: #fff;
          }

          & .info-label {
            font-size: 12px;
            color: #fff;
            display: flex;
            width: 70px;
            height: 19px;
            flex-direction: column;
            justify-content: center;
          }
          & .info-value {
            color: #fff;
            //display: flex;
            //flex-direction: column;
            //justify-content: center;
            //height: 19px;

            & /deep/ .ui-select {
              .item--wrap {
                height: 26px;
                line-height: 23px;
                padding-left: 0;
                box-shadow: none;

                .selected-item {
                  & > span {
                    margin-left: 6px;
                    margin-right: 24px;
                    height: 26px;
                    line-height: 26px;
                    color: #BBB;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;

                    & > .additional,
                    & > .name {
                      color: #2F2F2F;
                      margin-top: -2px;
                    }
                  }

                  .icon {
                    height: 16px;
                  }
                }

                .item__list {
                  border: none;

                  & .item__list-wrap {
                    max-height: 200px;
                  }

                  & .item__list-item {
                    height: 26px;
                    padding: 2px 0 0 10px;
                  }
                }
              }

            }

            &.target {
              margin-top: -1px;
              width: 198px;
              font-size: 14px;

              & .compare-date-picker-wrap {
                position: absolute;
                top: 28px;
                left: 30px;
              }
            }

            & > span {
              display: inline-block;
              width: 170px;
            }

            & .target-input {
              display: flex;
              height: 22px;
              padding: 2px 6px;
              align-items: center;
              flex-direction: row;
              gap: 6px;
              flex: 1 0 0;
              border-radius: 4px;
              background: #FFF;

              & > span {
                color: #BBB;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 100%; /* 14px */

                &.value {
                  color: #2F2F2F;
                }
              }

              &.period {
                & .value {
                  font-size: 13px;
                  letter-spacing: -0.1px;
                }
              }
            }
          }

          & .ellipsis {
            width: 170px;
          }

          & > .icon {
            position: absolute;
            top: 5.5px;
            right: 8px;
            font-size: 16px;
            cursor: pointer;
          }
        }
      }
    }
  }

  &  > div {
    box-sizing: border-box;
  }
  .error-label {
    width: calc(100% - 16px);
    padding: 4px 5px;
    margin: 8px 8px 0;
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: #F4E43A;
    border-radius: 5px;
    font-size: 12px;
    color: #444;
    z-index: 8;
    i.icon {
      background-color: #fff;
      border-radius: 50%;
    }
  }
  .clip-on-card {
    position: absolute;
    width: 18px;
    height: 47px;
    top: -12px;
    right: 44px;
    z-index: 10;
    transition: transform .5s;
    transform-origin: 50% 50%;
    &.click {
      transform: rotate(45deg);
      svg {
        path:first-child {
          d: path('M 1 23 L 1 38 A 7 7 0 0 0 15 38 L 15 -1');
        }
        path:last-child {
          d: path('M 10 7 L 10 1');
        }
      }
    }
  }
  .corner-button {
    position: absolute;
    display: inline-block;
    top: 0px;
    right: 0px;
    i {
      position: absolute;
      top: 6px;
      right: 6px;
      margin: 6px;
    }
    &.goto {
      width: 66px;
      height: 66px;
      background: none;
      clip-path: polygon(0 0, 100% 0, 100% 100%);
      border-top-right-radius: 10px;
      transition: none;
      &:hover {
        background: linear-gradient(45deg, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0) 60.61%), rgba(0, 0, 0, 0.44);
      }
    }
    &.dashboard {
      background-color: #F4F4F4;
      &:hover {
        background:#0074FF;
      }
    }
  }

  &:not(.title-edit-mode) {
    .mine-card-header:not(:has(.editable-area:hover)):hover ~ .corner-button.goto,
    .mine-card-body:not(:has(.marketing-info-btn:hover)):hover ~ .corner-button.goto {
      background: linear-gradient(45deg, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0) 60.61%), rgba(0, 0, 0, 0.44);
    }

    .mine-card-header:not(:has(.editable-area:hover)):hover ~ .corner-button.dashboard,
    .mine-card-body:not(:has(.marketing-info-btn:hover)):hover ~ .corner-button.dashboard {
      background: #0074FF;
    }
  }
  .mine-card-header {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;

    width: 100%;
    height: 30%;
    background-color: $reportColor;
    color: #fff;
    padding: 16px 20px;

    .mine-main-info {
      flex: 1;

      /deep/ .title-input {
        margin-top: -4px;
        pointer-events: auto;
        .label {
          display: none;
        }
        .input-element {
          input {
            height: 32px;
            border-radius: 5px;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.20);
            font-size: 13px;
            line-height: 32px;
            &:focus {
              border-color: transparent;
            }
          }
        }
      }
    }

    .mine-state {
      height: 30px;
      display: inline-flex;
      gap: 8px;
      .icon--new-small {
        position: relative;
        top: 3px;
      }
      .state-label {
        height: 12px;
        display: inline-block;
        color: #EC0047;
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        padding: 4px 6px;
        vertical-align: baseline;
        border-radius: 4px;
        background-color: #fff;
        &.report {
          &:first-letter { text-transform: uppercase; }
        }

        &.gray-border {
          border:1px solid rgba(255, 255, 255, 0.24);
          background: transparent;
          color: #ccc;
        }
        &.compare {
          position: relative;
          display: inline-flex;
          height: 12px;
          padding: 4px 6px 4px 4px;
          justify-content: center;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
          border-radius: 4px;
          outline: 1px solid rgba(255, 255, 255, 0.36);
          background: #EC0047;

          & > span {
            color: #FFF;
            text-align: center;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 100%; /* 12px */
          }
        }
        &.report-analyze {
          padding: 3px 6px;
          border: 1px solid rgba(0, 0, 0, 0.12);
        }
        &.mashup {
          background-color: $mashupColor;
        }
        &.analyze {
          padding: 3px 6px;
          color: $analyzeColor;
          border: 1px solid rgba(0, 0, 0, 0.12);
          background-color: #fff;;
        }
        // &.error {
        //   color: $errorColor;
        //   border-color: rgba(0, 0, 0, 0.12);
        //   background-color: #fff;
        //   pointer-events: auto !important;
        //   cursor: pointer !important;
        //   opacity: 1 !important;
        // }
      }
    }
    .date-view-info {
      display: flex;
      align-items: center;
      gap: 10px;
      opacity: .7;
      margin-bottom: 13px;
      font-size: 12px;
      .view-count-info {
        display: flex;
        align-items: center;
        .icon {
          margin-right: 4px;
        }
        &:before {
          content: '';
          display: inline-block;
          width: 8px;
          height: 8px;
          border-left: 1px solid rgba(255, 255, 255, .4);
        }
      }
    }
    .mine-title {
      position: relative;
      color: #fff;
      font-size: 20px;
      font-weight: 600;
      line-height: 1.2em;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.20);
      & span.version {
        position: absolute;
        right: 0;
        bottom: 0;
      }
      & span.title {
        overflow: hidden;
        text-overflow: ellipsis;
        max-height: 2.4em;
        overflow-wrap: break-word;
        max-width: 280px;
        width: 280px;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;

        & .highlight {
          color: #EC0047;
        }
      }
    }
    .editable-area {
      position: relative;
    }
  }
  .mine-card-body {
    flex-grow: 1;
    padding: 20px;
    .mine-info-column {
      display: flex;
      border-bottom: 1px dashed #d7d7d7;
      padding: 6px 0;
      gap: 12px;
      box-sizing: border-box;
      flex-direction: row;
      position: relative;
      flex-grow: 1;
      &.target {
        height: 63px;
      }
      & .column-container {
        & .target-column {
          position: absolute;
          display: flex;
          gap: 12px;
          top: 32px;
          width: 100%;

          & .info-label {
            width: 70px;
          }
          & .info-value {
            margin-left: 0;
          }
        }
        &.target {
          height: 58px;
          background-color: #FFEDF2;
          border: 1px solid #FFC4D5;
          position: absolute;
          top: 2px;
          left: -4px;
          width: 296px;
          -webkit-box-sizing: border-box;
          box-sizing: border-box;
          border-radius: 4px;
          vertical-align: middle;
          padding: 4px 8px;
          margin-left: -4px;

          &.error {
            background-color: #F4F4F4;
            border-color: #EBEBEB;

            & .info-label,
            & .info-value {
              & > span {
                color: #ccc;
              }
            }
          }

          & .info-label,
          & .info-value {
            & > span {
              color: #2F2F2F;
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: 150%; /* 18px */
            }

            &.upper {
              &:first-letter {
                text-transform: uppercase;
              }
            }
          }
          & .info-label {
            margin: initial;
            span {
              margin: 2px 0 0 0;
              font-size: 12px;
            }
          }
        }
      }
      &:last-child {
        border: 0;
      }
      &.marketing {
        .info-value {
          .marketing-info-btn {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            pointer-events: all;
            &.show i {
              transform: rotate(180deg);
            }
          }
        }
      }
      &.period {
        .date-range {
          line-height: 1.5;
          + .date-range {
            margin-top: 4px;
          }
        }

        .date-compare-btn {
          cursor: pointer;
          display: flex;
          max-width: 171px;
          padding: 6px;
          justify-content: center;
          align-items: center;
          border-radius: 6px;
          border: 1px solid #C1C1C1;
          background-color: #fff;
          line-height: 1;

          color: #444;
          font-size: 13px;
          font-weight: 600;
        }
      }

      & .column-container {
        display: flex;
        gap: 12px;
        flex-grow: 1;
        align-items: flex-start;

        & > .info-label {
          width: 70px;
          vertical-align: middle;
          margin: auto 0;
          display: inline-block;
          span {
            width: 70px;
            color: #767676;
            font-size: 12px;
            display: inline-block;
            line-height: 12px;
          }
        }
        & > .info-value {
          flex-grow: 1;
          max-width: 198px;
          color: #444;
          font-size: 14px;
          span {
            display: block;

            &.upper {
              &:first-letter {
                text-transform: uppercase;
              }
            }
          }
        }

        & > .search-exist {
          display: inline-block;
          &:first-letter {
            text-transform: uppercase;
          }
        }

        &.error {
          & .info-label {
            & > span {
              color: #cccccc;
            }
          }
        }
      }

      &:first-child {
        padding-top: 0;
      }
    }
  }
  .footer-status {
    position: relative;
    width: 100%;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    padding: 10px;
    background-color: #F8F8F8;
    color: #EC0047;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5%;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    &.compare-available {
      background-color: #222;
      padding: 0 10px 10px 10px;

      & .ui-btn {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        padding: 0 20px;

        font-size: 16px;
        font-weight: 600;
        &:after,
        &:before {
          border-radius: 4px;
        }
      }
    }

    &:not(.compare-available):before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: rgba(170, 170, 170, 0.1);
      border-top: 1px solid #e7e7e7;
    }
    &:not(.compare-available):hover {
      background-color: #EBEBEB;
    }

    &.analyzing {
      color: #999999;
    }

    &.disabled {
      color: #999999;
      pointer-events: none;
    }

    &.error {
      color: #d7d7d7;
    }
  }

  &.analyzing:not(.mashup):not(.dashboard):not(.cxTrends) {
    cursor: default;
    & > div:not(.error-control) {
      pointer-events: none !important;
    }
    .corner-button {
      cursor: default;
    }
    .mine-card-header {
      position: relative;
      background-color: #fff;
      .mine-title {
        color: #444;
        text-shadow: none;
      }
      .date-view-info {
        color: rgba(0, 0, 0, 0.44);
        .view-count-info:before {
          border-color: rgba(0, 0, 0, 0.2) !important;
        }
      }
      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: rgba(170, 170, 170, 0.1);
        border-bottom: 1px solid #e7e7e7;
      }
    }
    .mine-card-body {
      .date-compare-btn {
        pointer-events: none;
      }
    }
    .mine-in-depth {
      pointer-events: none;
      color: #d7d7d7;
    }
  }

  &.mashup {
    pointer-events: none;
    .ai-report-mark,
    .mine-in-depth,
    .clip-on-card {
      display: none;
    }
    .date-compare-btn {
      opacity: .3;
    }
  }
  &:not(.mashup) {
    .mine-card-header {
      .mine-title {
        pointer-events: auto;
        .title-edit-button {
          display: none;
          position: absolute;
          right: 0;
          top: -2px;
          justify-content: center;
          align-items: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background-color: #111;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.20);
          cursor: pointer;
          &:hover {
            background-color: #2F2F2F;
          }
        }
      }
    }
  }
  .mine-popup {
    width: 412px;
    height: 270px;
    position: absolute;
    left: 50%;
    top: 325px;
    background-color: #fff;
    transform: translateX(-50%);
    box-shadow: 0px 8px 15px 0px rgba(0, 0, 0, 0.16);
    z-index: 110;
    // padding: 20px 10px;
    transition: all 250px ease;
    .mine-marketing-detail {
      height: 100%;
      overflow-y: auto;
      color: #444;
      .contents {
        height: 1200px;
      }
    }
  }
  .retry-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9;
  }
  &.error {
    color: #ccc;
    cursor: default;
    & > div:not(.error-control) {
      pointer-events: none !important;
    }
    .corner-button {
      display: none;
    }
    .mine-card-header {
      position: relative;
      background-color: #fff !important;
      .date-view-info {
        color: rgba(0, 0, 0, 0.44);
        .view-count-info:before {
          border-color: rgba(0, 0, 0, 0.2) !important;
        }
      }
      .state-label {
        &.report {
          background-color: #ebebeb !important;
          color: #999 !important;
          border: 0;
          padding: 4px 6px;
        }
        &:not(.report):not(.reactive) {
          display: none !important;
        }
      }
      .mine-title {
        color: #ccc !important;
        text-shadow: none;
        &:after {
          color: #000 !important;
        }
        &:hover {
          color: #888 !important;
        }
      }
      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: rgba(170, 170, 170, 0.1);
        border-bottom: 1px solid #e7e7e7;
      }
    }
    .mine-card-body {
      .column-container{
        & .info-label,
        & .info-value {
          color: #ccc;
          span {
            color: #ccc;
          }
          .date-compare-btn {
            color: #ccc;
            border-color: #ccc;
          }
          .marketing-info-btn {
            i.icon { opacity: .44;}
          }
        }
      }
    }
  }

  /* ===== 컨텐츠 카드 ===== */
  &.cxTrends {
    &.analyzing {
      cursor: default;
    }
  }
  .mine-content {
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    position: relative;
    flex: 1;
    border-radius: 10px;

    .date-view-info {
      margin-bottom: 0;
    }

    .mine-main-info {
      flex: 0;

      .mine-title {
        display: flex;
        flex-direction: column;
        margin-top: 0;
        transform: translateY(0)!important;
      }
    }

    .mine-bottom-deco {
      right: 20px;
      bottom: 34px;
      display: flex;
      align-items: center;
      gap: 6px;
      justify-content: flex-start;
      flex-direction: column;
      color: #fff;
      font-weight: 600;
      text-align: center;

      .label {
        font-size: 12px;
        line-height: 120%;
      }
      .vol {
        font-size: 40px;
        line-height: 100%;
      }
    }

    /* 컨텐츠 카드 - cx data */
    &-cxtrends {
      position: relative;
      .date-view-info {
        color: #BBB;
        .view-count-info{
          &:before {
            border-color: #444;
            opacity: 1;
          }
        }
      }

      .mine-main-info {
        position: relative;
        padding-top: 83px;

        .mine-state {
          margin-top: 13px;
          margin-bottom: 49px;
          position: absolute;
          top: 0;
        }

        .mine-title {
          min-height: 0;
          gap: 51px;

          .title-wrap {
            min-height: 114px;
            display: flex;
            align-items: flex-start;
            color: #fff;
            font-size: 32px;
            font-weight: 700;
            line-height: 120%;
            letter-spacing: -0.48px;
          }

          .prompt-text {
            color: #fff;
            text-align: right;
            padding-right: 4px;
            font-size: 16px;
            font-weight: 700;
            line-height: 100%;
          }
        }
      }

      .mine-bottom-deco {
        color: #ffd900;
        position: unset;
        margin-top: 30px;

        margin-right: 40px;
        margin-left: auto;
      }

      &.mini {
        .mine-bottom-deco {
          margin: 0;
          position: absolute;
          align-items: flex-end;

          .label {
            color: #fff;
            font-size: 16px;
            font-weight: 700;
            line-height: 100%;
          }

          .desc {
            font-size: 30px;
          }
        }
      }

      &.small-card-title, {
        .mine-title {
          .title-wrap {
            font-size: 28px;
            letter-spacing: -0.42px;
          }
        }
      }
    }
  }
}

@each $report, $value in $reportType {
  $bg: nth($value, 1);
  $btn: nth($value, 2);
  $txt: nth($value, 3);
  $gradientBg1: nth($value, 4);
  $gradientBg2: nth($value, 5);
  $gradientBg3: nth($value, 6);
  $bgImg: nth($value, 7);

  @property --stop1 {
    syntax: '<percentage>';
    initial-value: 1%;
    inherits: false;
  }

  @property --stop2 {
    syntax: '<percentage>';
    initial-value: 25%;
    inherits: false;
  }

  @property --stop3 {
    syntax: '<percentage>';
    initial-value: 50%;
    inherits: false;
  }

  .#{$report} {
    .mine-card-header {
      background-color: $bg;
    }

    &.weekly:not(.analyzing):not(.no-data) {
      background: $bgImg bottom no-repeat,
        linear-gradient(317deg,
          $gradientBg1 var(--stop1),
          $gradientBg2 var(--stop2),
          $gradientBg3 var(--stop3));
          transition: --stop1 360ms, --stop2 360ms, --stop3 360ms;
      &:hover:not(.error) {
        --stop1: 25.5%;
        --stop2: 50.5%;
        --stop3: 74.5%;
      }
    }
    &.dashboard:not(.no-data) {
      background: linear-gradient(317deg,
          $gradientBg1 var(--stop1),
          $gradientBg2 var(--stop2),
          $gradientBg3 var(--stop3));
      transition: --stop1 360ms, --stop2 360ms, --stop3 360ms;
      &:hover:not(.error) {
        --stop1: 25.5%;
        --stop2: 50.5%;
        --stop3: 74.5%;
      }
      &.analyzing {
        background: linear-gradient(134deg, #777 0%, #999 50.48%, #AAA 100%);
      }
    }
  }
}

.ellipsis {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  word-break: break-all;
}
</style>
